import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import Logo from "../img/Logo_signcast_big-1080x322.png";
import mapIcon from "../img/mao icon-1080x322.png";
import useApiStore from "../store/apiStore";
import useAuthStore from "../store/authStore";
import { useSheetDataStore } from "../zustand/sheetDataStore";
import useExcelData from "../hook/formData";

const InfoTable = () => {
  const apiStore = useApiStore();
  const { user } = useAuthStore();
  const fullName = user?.fullName || "User";
  
  // Add refs to track initialization states
  const dateInitializedRef = useRef(false);
  const drawnInitializedRef = useRef(false);
  
  // Add ref to track user-edited fields
  const userEditedFieldsRef = useRef({});
  
  // Get sheet data from the Excel file
  const { sheetData, loading } = useExcelData(
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vQH7Uju3LbhqpO7joSwpTvCmAQMK79pspbH_Qnc1pNgMUUk-jFzvE1DSOBedsYc5l21It8bsE7yX3X6/pub?output=xlsx"
  );
  
  const {
    selectedScreen,
    selectedMediaPlayer,
    selectedMount,
    isNiche,
    isHorizontal,
  } = useSheetDataStore();

  // Get Pseudonyms for the selected items
  const screenPseudonym = selectedScreen && selectedScreen["Pseudonym"] ? selectedScreen["Pseudonym"] : "";
  const mountPseudonym = selectedMount && selectedMount["Pseudonym"] ? selectedMount["Pseudonym"] : "";
  const mediaPlayerPseudonym = selectedMediaPlayer && selectedMediaPlayer["Pseudonym"] ? selectedMediaPlayer["Pseudonym"] : "";

  // Derive display values for orientation and mounting based on boolean flags
  const orientationDisplayValue = isHorizontal !== undefined 
    ? (isHorizontal ? 'Horizontal' : 'Vertical') 
    : '';
  const mountingInOnDisplayValue = isNiche !== undefined 
    ? (isNiche ? 'Niche' : 'Flat wall') 
    : '';

  // Local state for form inputs with empty initial values
  const [tableData, setTableData] = useState({
    drawn: "",
    date: "",
    sheet: "",
    revision: "",
    department: "",
    drawingNumber: "",
    screen: "",
    mount: "",
    mediaPlayer: "",
    mountingInOn: "",
    orientation: ""
  });
  
  // Add this effect to reset the userEditedFields tracking after a successful load
  useEffect(() => {
    // Check if this was triggered by a load action (you'll need to add this flag to your store)
    if (apiStore.justLoaded) {
    
      userEditedFieldsRef.current = {}; // Reset the edited fields tracking
      
      // Your store should have a way to reset this flag after we've handled it
      if (typeof apiStore.resetJustLoaded === 'function') {
        apiStore.resetJustLoaded();
      }
    }
  }, [apiStore.infoTableData]);
  
  // Sync with apiStore when it changes, without causing a loop
  useEffect(() => {
    if (apiStore.infoTableData) {

   
      
      // Create a safe copy of the current table data before merging
      const currentTableData = { ...tableData };
      
      // Store the current user edits before updating from API
      const currentEdits = { ...userEditedFieldsRef.current };
      
      // Special case for the 'drawn' field - check if it's a user login change
      const isUserLoginChange = user?.fullName && 
                                currentTableData.drawn && 
                                currentTableData.drawn !== user.fullName && 
                                apiStore.infoTableData.drawn !== user.fullName;
      
      if (isUserLoginChange) {
      
        // Force update the drawn field with the new user's name
        const updatedData = {
          ...apiStore.infoTableData,
          drawn: user.fullName
        };
        
        // Update local state with the complete infoTable data
        setTableData(prev => ({
          ...prev,
          ...updatedData
        }));
        
        // Also update the API store
        apiStore.setInfoTableData(updatedData);
      } else {
        // Create the merged data object
        const mergedData = { ...apiStore.infoTableData };
        
        // CRITICAL: Explicitly preserve the modified/edited fields
        Object.keys(currentEdits).forEach(field => {
          // If the field is marked as edited, preserve the user's version
          if (currentEdits[field]) {
           
            mergedData[field] = currentTableData[field];
          }
        });
        
        // All fields are now treated equally, no special handling for screen/mount/mediaPlayer
        // Just rely on the userEditedFields tracking
        
        // Update the state
        setTableData(mergedData);
      }
      
      // Preserve the user edited fields tracking
     
      userEditedFieldsRef.current = currentEdits;
      
      // Make sure date initialization doesn't override our loaded date
      if ('date' in apiStore.infoTableData) {
        dateInitializedRef.current = true;
      }
    }
  }, [apiStore.infoTableData, user?.fullName]);

  // Initialize with apiStore data when component mounts
  useEffect(() => {
    if (apiStore.infoTableData) {
     
      
      // If apiStore has a date, log it
      if ('date' in apiStore.infoTableData) {
       
        dateInitializedRef.current = true; // Mark date as initialized
      }
      
      // Store the current user edits before updating
      const currentEdits = { ...userEditedFieldsRef.current };
      const hasUserEdits = Object.keys(currentEdits).length > 0;
      
      // Create a copy of the API data
      let initialData = { ...apiStore.infoTableData };
      
      // Special case for the 'drawn' field on initial load
      // If we have a user, but the drawn field doesn't match, update it
      if (user?.fullName && (!initialData.drawn || initialData.drawn !== user.fullName)) {
        
        initialData.drawn = user.fullName;
      }
      
      // If we have user edits, preserve them even on initial load
      if (hasUserEdits) {
       
        
        // For each edited field, preserve the current value
        Object.keys(currentEdits).forEach(field => {
          if (currentEdits[field] && tableData[field] !== undefined) {
           
            initialData[field] = tableData[field];
          }
        });
      }
      
      setTableData(prev => ({
        ...prev,
        ...initialData
      }));
      
      // Preserve user edits
      userEditedFieldsRef.current = currentEdits;
    } else if (user?.fullName) {
      // If we don't have apiStore data but we do have a user, initialize the drawn field
      
      setTableData(prev => ({
        ...prev,
        drawn: user.fullName
      }));
    }
  }, []); // Empty dependency array means this runs once on mount

  // Initialize the date field with current date if it's not already set
  useEffect(() => {
    // Only run once
    if (dateInitializedRef.current) return;
    
    
    
    // Skip initialization if we already have a date value in apiStore
    if (apiStore.infoTableData && 'date' in apiStore.infoTableData) {
     
      dateInitializedRef.current = true;
      return;
    }
    
    // Only set today's date if no date exists at all
    // Format the current date as MM/DD/YYYY
    const today = new Date();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const year = today.getFullYear();
    const formattedDate = `${month}/${day}/${year}`;
    
   
    
    const newData = {
      ...tableData,
      date: formattedDate
    };
    
    setTableData(newData);
    apiStore.setInfoTableData(newData);
    
    dateInitializedRef.current = true;
  }, [apiStore.infoTableData, tableData]); // Dependencies

  // Initialize the drawn field with the user's name if it's not already set
  useEffect(() => {
    // Reset initialization flag when user changes
    if (drawnInitializedRef.current && user?.fullName) {
      const currentUserName = tableData.drawn;
      
      // If the current name is different from the logged-in user,
      // and it matches a previous user (not a custom edit), update it
      if (currentUserName && currentUserName !== user.fullName) {
        // Check if this was a user login change rather than a custom edit
        // We do this by checking if the current name matches any previously logged-in user
        const isPreviousUserName = true; // Assume it's from a previous user login
        
        if (isPreviousUserName) {
         
          drawnInitializedRef.current = false; // Reset to allow re-initialization
        }
      }
    }
    
    // Only run initialization if not already initialized
    if (!drawnInitializedRef.current) {
      if (user?.fullName) {
       
        const newData = {
          ...tableData,
          drawn: user.fullName
        };
        setTableData(newData);
        apiStore.setInfoTableData(newData);
        
        drawnInitializedRef.current = true;
      }
    }
  }, [user?.fullName, tableData]); // React to user changes

  // Update fields when Zustand selections change - use a ref to prevent infinite updates
  const prevSelectionsRef = useRef({ 
    screen: '', 
    mount: '', 
    mediaPlayer: '',
    orientation: '',
    mountingInOn: ''
  });

  useEffect(() => {
    // Skip optimization if no boolean flags set
    if (isHorizontal === undefined && isNiche === undefined) {
      return;
    }
    
    // Skip if orientation and mountingInOn match previous values
    if (orientationDisplayValue === prevSelectionsRef.current.orientation &&
        mountingInOnDisplayValue === prevSelectionsRef.current.mountingInOn) {
      return;
    }
    

    
    // Make a copy of the data without referencing tableData to prevent the dependency loop
    const newData = { 
      ...apiStore.infoTableData || {} 
    };
    
    let hasChanges = false;

    // ONLY process orientation and mountingInOn fields
    // We've removed the screen, mount, and mediaPlayer processing
    
    // Orientation field
    const shouldUpdateOrientation = orientationDisplayValue && 
      (!newData.orientation || newData.orientation !== orientationDisplayValue) && 
      !userEditedFieldsRef.current.orientation;
 
    
    if (shouldUpdateOrientation) {
      newData.orientation = orientationDisplayValue;
      hasChanges = true;
    }
    
    // Mounting In/On field
    const shouldUpdateMountingInOn = mountingInOnDisplayValue && 
      (!newData.mountingInOn || newData.mountingInOn !== mountingInOnDisplayValue) && 
      !userEditedFieldsRef.current.mountingInOn;
 
    if (shouldUpdateMountingInOn) {
      newData.mountingInOn = mountingInOnDisplayValue;
      hasChanges = true;
    }

    if (hasChanges) {
     
      // Store current values to prevent unnecessary updates
      prevSelectionsRef.current = {
        ...prevSelectionsRef.current,
        orientation: orientationDisplayValue,
        mountingInOn: mountingInOnDisplayValue
      };
      
      setTableData(newData);
      apiStore.setInfoTableData(newData);
    } else {
      console.log('No changes made - edited fields preserved');
    }
  }, [
    orientationDisplayValue,
    mountingInOnDisplayValue,
    isHorizontal,
    isNiche,
    apiStore
  ]); // Removed the screen, mount, and mediaPlayer dependencies

  // Move the ref outside the useEffect
  const initialSaveRef = useRef(true);

  useEffect(() => {
    // Only run this effect once on initial mount
    if (!initialSaveRef.current) return;
    initialSaveRef.current = false;
    
    // Check if we already have data in MongoDB (apiStore)
    const hasApiData = apiStore.infoTableData && Object.keys(apiStore.infoTableData).length > 0;
    
    if (!hasApiData) {
      // Create data with defaults
      const today = new Date();
      const formattedDate = today.toLocaleDateString('en-US');
      
      // Create a complete data object with only date and drawn pre-filled
      // All other fields will be manual entry only
      const initialData = {
        ...tableData,
        drawn: fullName || tableData.drawn,
        date: formattedDate || tableData.date,
        // Only include orientation and mountingInOn from Zustand selections
        orientation: orientationDisplayValue || tableData.orientation,
        mountingInOn: mountingInOnDisplayValue || tableData.mountingInOn
        // screen, mount, mediaPlayer, sheet, revision, etc. will all be empty until manually filled
      };
      
     
      
      // Update state first
      setTableData(initialData);
      
      // Then save to API store which will persist to MongoDB
      // Use setTimeout to avoid potential race conditions
      setTimeout(() => {
        apiStore.setInfoTableData(initialData);
      }, 0);
    }
  }, []); // Empty dependency array = only run once on mount

  // Fixed content that's not editable
  const fixedContent = {
    address: "361 Steelcase RD W #1 , MARKHAM, ONTARIO",
    phone: "Phone: (416) 900-2263"
  };

  // Update field handler that will be called on blur
  const updateField = (field, value) => {
    // Add mountingInOn, orientation, and drawn to protected fields
    const isProtectedField = field === 'screen' || field === 'mount' || field === 'mediaPlayer' ||
                             field === 'mountingInOn' || field === 'orientation' || field === 'drawn';
    if (isProtectedField) {
      console.log(`Field ${field} is protected from manual edits`);
      return;
    }
    
    // Skip update if value hasn't changed to prevent unnecessary renders
    if (tableData[field] === value) {
      console.log(`Field ${field} unchanged, skipping update`);
      return;
    }
    
   
    
    // Mark this field as user-edited (and log it to confirm)
    // Important: Don't update the ref until we know we're making a change
    userEditedFieldsRef.current[field] = true;
    
    
    // Use functional update to ensure we're working with the latest state
    setTableData(prevData => {
      const newData = {
        ...prevData,
        [field]: value
      };
      
      // Only update API store if we're actually changing the value
      apiStore.setInfoTableData(newData);
      
      return newData;
    });
  };

  // StyledInput component with CSS styling applied directly
  const StyledInput = ({ field }) => {
    return (
      <input
        type="text"
        value={tableData[field]}
        onChange={(e) => updateField(field, e.target.value)}
        className="w-full outline-none text-center text-[11px] focus:border-b focus:bg-gray-50 focus:border-gray-300 print:text-[9px] print:border-none"
        style={{
          backgroundColor: 'transparent',
          transition: 'background-color 0.1s, border 0.1s',
          height: '100%',
          minHeight: '16px',
          position: 'relative',
          zIndex: 1,
          cursor: 'text',
          padding: '0px 1px'
        }}
        // Add data attribute to make it easier to target with CSS
        data-print-field={field}
      />
    );
  };



  // Add effect to update the table data with pseudonyms when selections change
  useEffect(() => {
    if (!loading) {
      // Check if any values have actually changed before updating
      const hasScreenChanged = tableData.screen !== screenPseudonym && screenPseudonym !== '';
      const hasMountChanged = tableData.mount !== mountPseudonym && mountPseudonym !== '';
      const hasMediaPlayerChanged = tableData.mediaPlayer !== mediaPlayerPseudonym && mediaPlayerPseudonym !== '';
      const hasOrientationChanged = tableData.orientation !== orientationDisplayValue && orientationDisplayValue !== '';
      const hasMountingChanged = tableData.mountingInOn !== mountingInOnDisplayValue && mountingInOnDisplayValue !== '';
      
      // Only update if something has actually changed
      if (hasScreenChanged || hasMountChanged || hasMediaPlayerChanged || 
          hasOrientationChanged || hasMountingChanged) {
        
        // Create a single update object with all changes
        const updates = {};
        if (hasScreenChanged) updates.screen = screenPseudonym;
        if (hasMountChanged) updates.mount = mountPseudonym;
        if (hasMediaPlayerChanged) updates.mediaPlayer = mediaPlayerPseudonym;
        if (hasOrientationChanged) updates.orientation = orientationDisplayValue;
        if (hasMountingChanged) updates.mountingInOn = mountingInOnDisplayValue;
        
        // Update local state
        setTableData(prev => ({
          ...prev,
          ...updates
        }));
        
        // Update API store in a separate operation to prevent infinite loop
        const apiData = apiStore.infoTableData || {};
        const apiUpdates = {
          ...apiData,
          ...updates
        };
        
        // Use setTimeout to break the update cycle
        setTimeout(() => {
          apiStore.setInfoTableData(apiUpdates);
        }, 0);
      }
    }
  }, [
    screenPseudonym, 
    mountPseudonym, 
    mediaPlayerPseudonym,
    orientationDisplayValue,
    mountingInOnDisplayValue,
    loading, 
    tableData, 
    apiStore
  ]);


  // Add an effect to update the drawn field when the user changes
  useEffect(() => {
    if (user?.fullName && tableData.drawn !== user.fullName) {
      // Only update if the user name has changed
    
      
      setTableData(prev => ({
        ...prev,
        drawn: user.fullName
      }));
      
      // Also update the API store
      const apiData = apiStore.infoTableData || {};
      const apiUpdates = {
        ...apiData,
        drawn: user.fullName
      };
      
      // Use setTimeout to break the update cycle
      setTimeout(() => {
        apiStore.setInfoTableData(apiUpdates);
      }, 0);
    }
  }, [user?.fullName, tableData.drawn, apiStore]);

  return (
    <div className="v-info-table-container w-full h-full overflow-hidden print:overflow-visible">
      <div className="v-info-table-container-inner border border-gray-300 border-b-1 shadow-sm bg-white h-full print:h-auto print:max-h-36 print:overflow-hidden">
        {/* Header with Logo and Address */}
        <div className=" v-info-table-header text-align-right flex items-start justify-between border-b border-gray-300 h-[57px] px-4 py-2 print:h-12 print:py-1">
          <div className="w-1/3">
            <img className="h-8 print:h-6  v-logo" src={Logo} alt="Signcast Media" />
          </div>
          <div className="w-2/3 text-right text-[11px] leading-[14px]">
            <div className="text-right w-full">{fixedContent.address}</div>
            
            <div className="text-right w-full">{fixedContent.phone}</div>
          </div>
        </div>

        {/* Main Table - With explicit print styling */}
        <table className="v-info-table w-full  text-[11px] print:text-[9px] print:w-full ">
          <tbody className="!border-r-red">
            {/* Row A - Drawn / Dimensions / Mounting/ Orientation Headers */}
            <tr className="v-info-table-header-titles h-[22px] print:h-4 font-extrabold">
              <td className="border-t-0 border border-gray-300 border-l-0 bg-white w-1/4 text-center p-0 print:p-0 table_input_td">
                Drawn
              </td>
              <td className="border-t-0 border border-gray-300 bg-white w-1/4 text-center p-0 print:p-0" rowSpan="2">
                <div className="v-map-icon-container flex flex-row items-center justify-center h-full">
                  <span className="v-dimension-info-table mr-1 border-r border-gray-300 print:text-[8px]">Dimensions<br />In Inches</span>
                  <img className="v-map-icon w-10 h-auto ml-2 print:w-6" src={mapIcon} alt="mapIcon"/>
                  <div className="absolute bottom-0 left-0 right-0">
                    {/* <TransparentInput field="dimensions" onUpdate={updateField}/> */}
                  </div>
                </div>
              </td>
              <td className="border-t-0 border border-gray-300 bg-white w-1/4 text-center p-0 print:p-0 table_input_td">
                Mounting In/On
              </td>
              <td className="border-t-0 border border-gray-300 border-r-0 bg-white w-1/4 text-center p-0 print:p-0 table_input_td">
                Orientation
              </td>
            </tr>
            
            {/* Row B - Values for Drawn / Mounting / Orientation */}
            <tr className=" h-[22px] print:h-4">
              <td className="border border-gray-300 border-l-0 text-center p-0 print:p-0">
                <TransparentInput field="drawn" defaultValue={fullName} onUpdate={updateField}/>
              </td>
              {/* Dimensions cell is handled in the rowspan above */}
              <td className="border border-gray-300 text-center p-0 print:p-0">
                <TransparentInput field="mountingInOn" defaultValue={mountingInOnDisplayValue} onUpdate={updateField}/>
              </td>
              <td className="border border-gray-300 border-r-0 text-center p-0 print:p-0">
                <TransparentInput field="orientation" defaultValue={orientationDisplayValue} onUpdate={updateField}/>
              </td>
            </tr>
            
            {/* Row C - Headers for Date / Screen / Mount / Media Player */}
            <tr className="v-info-table-header-titles h-[22px] print:h-4 font-bold">
              <td className="border border-gray-300 border-l-0 bg-white text-center p-0 print:p-0 table_input_td">
                Date
              </td>
              <td className="border border-gray-300 bg-white text-center p-0 print:p-0 table_input_td">
                Screen
              </td>
              <td className="border border-gray-300 bg-white text-center p-0 print:p-0 table_input_td">
                Mount
              </td>
              <td className="border border-gray-300 bg-white text-center border-r-0 p-0 print:p-0 table_input_td">
                Media Player
              </td>
            </tr>
            
            {/* Row D - Values for Date / Screen / Mount / Media Player */}
            <tr className="h-[22px] print:h-4">
              <td className="border border-gray-300 border-l-0 text-center p-0 print:p-0">
                <TransparentInput field="date" defaultValue={new Date().toLocaleDateString('en-US')} onUpdate={updateField}/>
              </td>
              <td className="border border-gray-300 text-center p-0 print:p-0">
                <TransparentInput field="screen" defaultValue={screenPseudonym} onUpdate={updateField}/>
              </td>
              <td className="border border-gray-300 text-center p-0 print:p-0">
                <TransparentInput field="mount" defaultValue={mountPseudonym} onUpdate={updateField}/>
              </td>
              <td className="border border-gray-300 border-r-0 text-center p-0 print:p-0">
                <TransparentInput field="mediaPlayer" defaultValue={mediaPlayerPseudonym} onUpdate={updateField}/>
              </td>
            </tr>
            
            {/* Row 5 - Headers for Sheet / Revision / Department / Drawing No */}
            <tr className="v-info-table-header-titles h-[22px] print:h-4 font-bold">
              <td className="border border-gray-300 border-l-0 bg-white text-center p-0 print:p-0 table_input_td">
                Sheet
              </td>
              <td className="border border-gray-300 bg-white text-center p-0 print:p-0 table_input_td">
                Revision
              </td>
              <td className="border border-gray-300 bg-white text-center p-0 print:p-0 table_input_td">
                Department
              </td>
              <td className="border border-gray-300 bg-white text-center border-r-0 p-0 print:p-0 table_input_td">
                Drawing No:
              </td>
            </tr>
            
            {/* Row 6 - Values for Sheet / Revision / Department / Drawing No */}
            <tr className="h-[22px] print:h-4 ">
              <td className="border border-gray-300 border-l-0 border-b-0 text-center p-0 print:p-0">
                <TransparentInput field="sheet" onUpdate={updateField}/>
              </td>
              <td className="border border-gray-300 border-b-0 text-center p-0 print:p-0">
                <TransparentInput field="revision" onUpdate={updateField}/>
              </td>
              <td className="border border-gray-300 border-b-0 text-center p-0 print:p-0">
                <TransparentInput field="department" onUpdate={updateField}/>
              </td>
              <td className="border border-gray-300 border-b-0 border-r-0 text-center p-0 print:p-0">
                <TransparentInput field="drawingNumber" onUpdate={updateField}/>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default InfoTable;
const TransparentInput = ({ field, defaultValue, onUpdate }) => {
  const apiStore = useApiStore();
  const [isFocused, setIsFocused] = useState(false);
  const initialRenderRef = useRef(true);
  
  // Add mountingInOn, orientation, and drawn to read-only fields
  const isReadOnly = field === 'screen' || field === 'mount' || field === 'mediaPlayer' || 
                     field === 'mountingInOn' || field === 'orientation' || field === 'drawn';
  
  // Use useMemo to create stable values and prevent unnecessary recalculations
  const value = useMemo(() => {
    // Special handling for date field
    if (field === 'date') {
      // If we have a date in apiStore, use that
      if (apiStore.infoTableData?.date) {
        return apiStore.infoTableData.date;
      }
      
      // Get today's date formatted as MM/DD/YYYY for the date field default
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      const year = today.getFullYear();
      return `${month}/${day}/${year}`;
    }
    
    // Special handling for screen, mount, and mediaPlayer fields
    if (field === 'screen' || field === 'mount' || field === 'mediaPlayer') {
      // First check if there's a value in the API store
      if (apiStore.infoTableData && field in apiStore.infoTableData && apiStore.infoTableData[field]) {
        return String(apiStore.infoTableData[field]);
      }
      // Otherwise use the defaultValue (which will be the pseudonym from our useEffect)
      else if (defaultValue) {
        return defaultValue;
      }
      return '';
    }
    
    // For all other fields - standard handling
    // First check if the value exists in apiStore
    if (apiStore.infoTableData && field in apiStore.infoTableData && apiStore.infoTableData[field]) {
      return String(apiStore.infoTableData[field]);
    }
    
    // Otherwise use the default value
    return defaultValue || '';
  }, [field, defaultValue, apiStore.infoTableData]);

  // Set default values for drawn and date fields (ONLY)
  useEffect(() => {
    // Only run this on the initial render
    if (initialRenderRef.current) {
      initialRenderRef.current = false;
      
      // Only set default values for date and drawn when they're missing in API
      const fieldMissingInStore = !apiStore.infoTableData || !apiStore.infoTableData[field];
      const hasDefaultValue = value && value.length > 0;
      
      if (((field === 'drawn' || field === 'date') || 
           (field === 'screen' || field === 'mount' || field === 'mediaPlayer')) 
          && fieldMissingInStore && hasDefaultValue) {
     
        
        // Use setTimeout to break the render cycle and prevent infinite loops
        setTimeout(() => {
          onUpdate(field, value);
        }, 10);
      }
    }
  }, [field, value, apiStore.infoTableData]); // Removed onUpdate from dependencies
  
  // MODIFY this handleChange function
  const handleChange = useCallback((e) => {
    // ADD THIS - Skip updating for read-only fields
    if (isReadOnly) return;
    
    const newValue = e.target.value;
    onUpdate(field, newValue);
  }, [field, onUpdate, isReadOnly]); // ADD isReadOnly to dependencies
  
  // MODIFY this handleFocus function
  const handleFocus = useCallback((e) => {
    // ADD THIS - Prevent focus for read-only fields
    if (isReadOnly) {
      e.preventDefault();
      e.target.blur();
      return;
    }
    
   
    setIsFocused(true);
  }, [field, isReadOnly]); // ADD isReadOnly to dependencies
  
  // handleBlur stays the same
  const handleBlur = useCallback(() => {
    setIsFocused(false);
  }, []);

  return (
    <div className="relative w-full h-full">
      <div className="absolute inset-0 flex items-center justify-center text-center text-black pointer-events-none table_input">
        {value}
      </div>
      <input
        type="text"
        value={value}
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        readOnly={isReadOnly} // ADD THIS attribute
        className={`v-info-table-input bg-transparent w-full h-full outline-none border-none text-center text-transparent ${isFocused ? 'focused-input' : ''} ${isReadOnly ? 'cursor-default' : 'cursor-text'}`} // ADD the cursor class
        style={{ 
          caretColor: isReadOnly ? "transparent" : "black", // MODIFY this line
          backgroundColor: isFocused ? 'rgba(240, 240, 255, 0.3)' : 'transparent'
        }}
      />
    </div>
  );
};